import { Component, ComponentInterface, Event, EventEmitter, h, Method, Prop } from "@stencil/core";
import { Check, CheckboxMixed } from "./checkbox-icons";

/**
 * @slot (default) - Default content shown as label.
 */
@Component({
    tag: "uic-checkbox",
    styleUrl: "checkbox.scss"
})
export class Checkbox implements ComponentInterface {
    private input: HTMLInputElement;

    /**
     * The text to show next to the checkmark.
     */
    @Prop() readonly label?: string;

    /**
     * If set to `true`, the user cannot interact with the checkbox.
     */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /**
     * If set to `true`, the checkbox is neither checked nor unchecked.
     * Mostly used in a hierarchy with multiple child checkboxes to indicate their different states.
     */
    @Prop({ reflect: true, mutable: true }) mixed = false;

    /**
     * If set to `true`, the checkbox is considered to be checked.
     * This property can be overridden by the `mixed` property.
     */
    @Prop({ reflect: true, mutable: true }) checked = false;

    /**
     * If set to `true`, the label will wrap.
     */
    @Prop({ reflect: true }) readonly multiline: boolean = false;

    /**
     * The value.
     */
    @Prop() readonly value = "1";

    /**
     * The name.
     */
    @Prop() readonly name: string;

    /**
     * Emitted when the state of the checkbox changes.
     */
    @Event() uicChange: EventEmitter;

    /**
     * Emitted when the checkbox gains focus.
     */
    @Event() uicFocus: EventEmitter;

    /**
     * Emitted when the checkbox loses focus.
     */
    @Event() uicBlur: EventEmitter;

    /**
     * Sets focus to the checkbox.
     * @param options The focus options.
     */
    @Method()
    async setFocus(options?: FocusOptions) {
        this.input.focus(options);
    }

    /**
     * Removes focus from the checkbox.
     */
    @Method()
    async removeFocus() {
        this.input.blur();
    }

    private handleChange() {
        this.checked = this.input.checked;
        this.mixed = this.input.indeterminate;
        this.uicChange.emit();
    }

    private handleBlur() {
        this.uicBlur.emit();
    }

    private handleFocus() {
        this.uicFocus.emit();
    }

    render() {
        return (
            <label class='uic-checkbox__wrapper'>
                <input
                    class='uic-checkbox__input'
                    ref={(e) => (this.input = e)}
                    type='checkbox'
                    disabled={this.disabled}
                    checked={this.checked}
                    indeterminate={this.mixed}
                    onChange={() => this.handleChange()}
                    onBlur={() => this.handleBlur()}
                    onFocus={() => this.handleFocus()}
                    value={this.value}
                    name={this.name}
                />
                <span class='uic-checkbox__mark'>
                    {Check("uic-checkbox__mark-check")}
                    {CheckboxMixed("uic-checkbox__mark-mixed")}
                </span>
                <span class='uic-checkbox__label'>
                    {this.label}
                    <slot />
                </span>
            </label>
        );
    }
}
